<template>
  <b-sidebar
    id="sidebar-request-letter-apply-doc-edit"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ $t('Update') }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="clear(hide)"
        />
      </div>

      <b-form
        class="p-2"
        @submit.prevent
      >
        <validation-observer ref="applyDocumentEdit">
          <b-form-group
            :label="$t('Dc Type')"
            label-for="dc_type"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Dc Type')"
              rules="required"
            >
              <v-select
                id="dc_type"
                v-model="applyDocument.dc_type"
                :reduce="option => option.id"
                :options="dcTypes"
                :get-option-label="option => $langName(option.name)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Organization')"
            label-for="organization"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Organization')"
              rules="required"
            >
              <v-select
                id="organization"
                v-model="applyDocument.organization"
                :reduce="option => option.id"
                :options="organizationOptions"
                :get-option-label="option => $langName(option.name)"
                @input="loadDirections"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group :label="$t('Status')">
            <validation-provider
              #default="{ errors }"
              :name="$t('Status')"
              rules="required"
            >
              <v-select
                v-model="applyDocument.status"
                :reduce="option => option.id"
                :options="statusOptions"
                :get-option-label="getStatusLabel"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Directions')"
            label-for="direction"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Directions')"
              rules="required"
            >
              <v-select
                id="direction"
                v-model="applyDocument.direction_id"
                :reduce="option => option.direction.id"
                :options="directionOptions"
                :get-option-label="option => option.direction ? `${option.direction.code} - ${$langName(option.direction.name)}` : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </validation-observer>

        <div class="d-flex mt-2">
          <b-button
            variant="primary"
            class="mr-2"
            type="submit"
            @click="submit(hide)"
          >
            {{ $t('Update') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="clear(hide)"
          >
            {{ $t('Cancel') }}
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BSidebar,
    BForm,
    BFormGroup,
    BButton,
    vSelect,
  },
  props: {
    requestLetterId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    applyDocument: {
      get() {
        return this.$store.state.applyDocument.applyDocument
      },
      set(data) {
        this.$store.commit('applyDocument/UPDATE', data)
      },
    },
    dcTypes() {
      return this.$store.state.staticModule.dcTypes
    },
    organizations() {
      return this.$store.state.applyDocument.organizations
    },
    organizationOptions() {
      const options = [...this.organizations]
      if (
        this.applyDocument.organization
        && this.applyDocument.organization_name
        && !options.some(option => option.id === this.applyDocument.organization)
      ) {
        options.unshift({
          id: this.applyDocument.organization,
          name: this.applyDocument.organization_name,
        })
      }
      return options
    },
    directions() {
      return this.$store.state.applyDocument.directions
    },
    directionOptions() {
      const options = [...this.directions]
      if (
        this.applyDocument.direction_id
        && this.applyDocument.direction_name
        && !options.some(option => option.direction && option.direction.id === this.applyDocument.direction_id)
      ) {
        options.unshift({
          direction: {
            id: this.applyDocument.direction_id,
            code: '',
            name: this.applyDocument.direction_name,
          },
        })
      }
      return options
    },
    staticStatuses() {
      const {
        applyDocumentAdmittedToStudy,
        applyDocumentHasGraduated,
        applyDocumentHasBeenCanceled,
        applyDocumentSuccessfullyDefended,
      } = this.$store.state.staticModule

      return this.$store.state.staticModule.applyDocumentStatuses.filter(status => [
        applyDocumentAdmittedToStudy,
        applyDocumentHasGraduated,
        applyDocumentHasBeenCanceled,
        applyDocumentSuccessfullyDefended,
      ].includes(status.id))
    },
    statusOptions() {
      if (Array.isArray(this.applyDocument.apply_status) && this.applyDocument.apply_status.length) {
        return this.applyDocument.apply_status.map(status => ({
          id: status[0],
          name: status[1],
        }))
      }
      return this.staticStatuses
    },
  },
  mounted() {
    localize(this.$i18n.locale)
    this.$store.dispatch('staticModule/dcType')
    this.$store.dispatch('staticModule/applyDocumentStatuses')
    this.$store.dispatch('applyDocument/organizations')
  },
  methods: {
    loadDirections() {
      this.applyDocument.direction_id = null
      if (this.applyDocument.organization) {
        this.$store.dispatch('applyDocument/directions', { org: this.applyDocument.organization, limit: 9999 })
      }
    },
    getStatusLabel(option) {
      if (typeof option.name === 'object') return this.$langName(option.name)
      const staticStatus = this.$store.getters['staticModule/getApplyDocumentStatus'](option.id)
      return staticStatus ? this.$langName(staticStatus.name) : option.name
    },
    clear(hide) {
      this.$store.commit('applyDocument/FRESH')
      this.$refs.applyDocumentEdit.reset()
      hide()
    },
    submit(hide) {
      this.$refs.applyDocumentEdit.validate().then(success => {
        if (!success) return

        const payload = {
          id: this.applyDocument.id,
          dc_type: this.applyDocument.dc_type,
          edu_lang: this.applyDocument.edu_lang,
          organization: this.applyDocument.organization,
          status: this.applyDocument.status,
          direction: this.applyDocument.direction_id,
        }

        this.$createSwal(this, payload, 'applyDocument/update', () => {
          const closeEditor = () => {
            this.$store.commit('applyDocument/FRESH')
            hide()
          }

          if (this.requestLetterId) {
            this.$store.dispatch('requestLetters/get', this.requestLetterId)
              .finally(closeEditor)
            return
          }

          this.$emit('refetchData')
          closeEditor()
        })
      })
    },
  },
}
</script>
